import type { ChangeEvent, MouseEvent } from 'react';
import { Dropdown, MenuItem } from 'react-bootstrap';
import { window } from '../../../globals';
import { Filter, ServiceFilter, ServiceQueryParameters } from '../../../types';
import utils from '../../../utils';
import Icon, { icons } from '../../generic/Icon';
import Button, { ButtonGroup, BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button';
import Flex from '../../generic/Flex';
import { ICON_SIZE } from '../../generic/Icon/icon-constants';
import ServiceFilters from './ServiceFilters';
import { ServiceMetadata } from './types';

type ServiceImportHeaderProps = {
  serviceDetails: ServiceMetadata;
  filters: ServiceFilter[];
  query: ServiceQueryParameters;
  instanceNames: string[];
  instanceIsConnected: boolean;
  selectedRepoLength: number;
  selectedFilter?: Filter;
  isLoading: boolean;
  searchValue: string;
  onSelectFilter: (filterType: string, filterValue: string) => Promise<void>;
  onSelectInstanceName: (instanceName: string) => Promise<void>;
  onSearchChange: (ev: ChangeEvent<HTMLInputElement>) => void;
  onImport: () => Promise<void>;
  onBulkImport: () => void;
  onConnectToService: (instanceName: string) => void;
};

const ServiceImportHeader = ({
  serviceDetails,
  instanceNames,
  selectedFilter,
  isLoading,
  selectedRepoLength,
  filters,
  searchValue,
  query,
  instanceIsConnected,
  onSelectInstanceName,
  onSelectFilter,
  onSearchChange,
  onImport,
  onBulkImport,
  onConnectToService,
}: ServiceImportHeaderProps) => {
  const serviceFilter = !instanceIsConnected && !isLoading
    ? (
      <Button
        icon={icons.link}
        type={BUTTON_TYPE.PRIMARY}
        size={BUTTON_SIZE.MEDIUM}
        onClick={onConnectToService}>
        Connect with Service
      </Button>)
    : (
      <ServiceFilters
        categories={filters}
        query={query}
        selectedFilter={selectedFilter}
        onSelect={onSelectFilter}
      />);

  return (
    <Flex justify="space-between" align="center">
      <ButtonGroup spacing="sm" direction="right">
        <div className="btn">
          <Flex align="center">
            <div>
              {/* Let font icon take precedence, but we should slowly adopt SVGIcon to render more complex Logos (e.g., AzureLogo). */}
              {serviceDetails.icon ?
                <Icon size={ICON_SIZE.XL} icon={icons[serviceDetails.icon]} /> :
                serviceDetails.SVGIcon && serviceDetails.SVGIcon({})}
            </div>
            <div>
              &nbsp;{serviceDetails.label}
            </div>
          </Flex>
        </div>
        {instanceNames.length > 1 &&
          <Dropdown
            id="service-instances"
            onSelect={onSelectInstanceName}
            className="service-import-header-btn"
          >
            <Dropdown.Toggle noCaret>
              <Flex justify="space-between">
                <div className="oneline">{query.instanceName}</div>
                <div className="caret" />
              </Flex>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {instanceNames
                .map(name => name && (
                  <MenuItem eventKey={name} active={name === query.instanceName}>
                    {name}
                  </MenuItem>
                ))}
            </Dropdown.Menu>
          </Dropdown>}
        {serviceFilter}
      </ButtonGroup>
      {instanceIsConnected &&
        <>
          <div className="input-search-wrapper">
            <input
              type="text"
              className="form-control input-search input-block-level"
              placeholder="Search repos"
              onChange={onSearchChange}
              value={searchValue}
            />
          </div>
          <div className="text-right">
            <Button
              size="medium"
              type="secondary"
              onClick={(ev: MouseEvent) => {
                if (!utils.verify_subscription('premium')) {
                  window.showPlanSelectorModal();
                  ev.preventDefault();
                } else {
                  onBulkImport();
                }
              }}
            >
              <Icon icon={icons.download} />{' '}Import All
            </Button>
            {' '}
            <Button
              size="medium"
              onClick={onImport}
              isDisabled={!selectedRepoLength || isLoading}
            >
              <b>Import</b>
              {' '}
              {isLoading ? (
                <Icon icon={icons.circleO} hasSpin />
              ) : selectedRepoLength > 0 && (
                <span>&nbsp;{selectedRepoLength}&nbsp;</span>
              )}
              {' '}
              <Icon icon={icons.caretRight} />
            </Button>
          </div>
        </>}
    </Flex>
  );
};

export default ServiceImportHeader;
